import { download, generateCsv, mkConfig } from "export-to-csv"
import { useDateTime } from "../../../../../localization/useDateTime"
import { useMoney } from "../../../../../currency/useMoney"
import { toDinero } from "../../../../../currency/currencyLib"
import { useSupplier } from "../../../../supplierHooks"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import {
    fetchMerchantSalesForExport,
    SupplierMerchantSalesForExportResponse,
    SupplierMerchantSalesQuery,
} from "../merchantSalesApi"
import { Account, Category, MerchantSalesMerchantsForExport } from "../merchantSalesTypes"

function transformData(data: SupplierMerchantSalesForExportResponse) {
    return data.merchants
}

export const useExportMerchantSales = (chosenStartDate: string, chosenEndDate: string) => {
    const dateTimeFactory = useDateTime()
    const moneyFactory = useMoney({})

    const { supplierId } = useSupplier()

    const { refetch } = useApiQuery(fetchMerchantSalesForExport, {
        queryName: SupplierMerchantSalesQuery.MERCHANT_SALES_FOR_EXPORT,
        dependencies: {
            supplierId,
            startDate: chosenStartDate,
            endDate: chosenEndDate,
        },
        enabled: false,
        select: transformData,
    })

    const formattedStartDate = dateTimeFactory.formatDate(dateTimeFactory.fromISO(chosenStartDate))
    const formattedEndDate = dateTimeFactory.formatDate(dateTimeFactory.fromISO(chosenEndDate))

    const csvOptions = mkConfig({
        fieldSeparator: ";",
        quoteStrings: true,
        decimalSeparator: ".",
        useBom: true,
        useKeysAsHeaders: true,
        filename: `Merchant_sales-${formattedStartDate}_${formattedEndDate}`,
    })

    const mapMerchantSalesToCsvRow = (
        merchant: MerchantSalesMerchantsForExport,
        category: Category,
        account: Account,
    ) => ({
        "Booked from": formattedStartDate,
        "Booked to": formattedEndDate,
        "Case number": merchant.caseNumber ?? "",
        Name: merchant.name ?? "",
        "Sales channel": category.name ?? "",
        "Payment method": account.name ?? "",
        "Upcoming Sale": account.upcomingSales ? moneyFactory.formatAsNumber(toDinero(account.upcomingSales)) : "",
        "Booked Sale": account.bookedSales ? moneyFactory.formatAsNumber(toDinero(account.bookedSales)) : "",
    })

    const handleExportRows = async (ids?: number[]) => {
        const { data } = await refetch()
        let exportRows: ReturnType<typeof mapMerchantSalesToCsvRow>[] = []

        if (data && data.length > 0) {
            const filteredData = ids ? data.filter((merchant) => ids.includes(merchant.id)) : data

            exportRows = filteredData.flatMap((merchant) =>
                merchant.salesByAccount.categories.flatMap((category) =>
                    category.accounts.map((account) => mapMerchantSalesToCsvRow(merchant, category, account)),
                ),
            )
        } else {
            exportRows = [
                mapMerchantSalesToCsvRow({} as MerchantSalesMerchantsForExport, {} as Category, {} as Account),
            ]
        }

        const csv = generateCsv(csvOptions)(exportRows)
        download(csvOptions)(csv)
    }

    return { handleExportRows }
}
